import {
    CreateSubmissionParameters,
    EntityNewestSubmissionResponse,
    Metadata,
    Operation,
    PendingSubmissionsResults,
    RequestCustomerDataCopyParameters,
    Submission,
    SubmissionDescriptor,
    SubmissionDetail,
    SubmissionPortalStatus,
    SubmissionSearchRequest,
    SubmissionSearchResponse,
    SubmissionSearchResult,
} from '../Models/Api/strongbox.financialportal';
import { FetchMethod, fetchWrapper } from '../Utils/FetchWrapper';

import { EmptyGuid } from '../Utils/Constants';
import { TestEmail } from '../Utils/FormUtils';
import { LogException, LogMessage, SeverityLevel } from '../Utils/Logging';

/**
 * Creates a submission
 *
 * @param entityId  entityId of the thing this submission will belong to.
 * @param submissionMeta  additional metadata to tag to the submission. 
 * @param verificationEmail if provided ensures the submission is in the unverified
 * state until verification is completed from an email sent to this address
 *
 * @result id of the submission.  
 * @throws on error.  FetchError from the fetch call or Error
 */

export async function CreateSubmission(
    entityId: string,
    submissionRequestId?: number,
    submissionMeta?: Map<string, string>,
    verificationEmail?: string
): Promise<string> {
    LogMessage(
        `CreateSubmission`,
        SeverityLevel.Information,
        {
            entityId,
            submissionRequestId,
        }
    );

    const url = `/api/Submissions`;

    if (!!verificationEmail) {
        if (!TestEmail(verificationEmail)) {
            throw new Error('The email provided for submission verification is invalid');
        }
    }

    let submissionMetadata: Metadata[] | undefined = undefined;
    if (!!submissionMeta) {
        submissionMetadata = [];
        submissionMeta.forEach((value, key) => {
            submissionMetadata!.push({
                name: key,
                value,
                type: 'None'
            });
        });
    }

    const request: CreateSubmissionParameters = {
        entityId,
        requestId: submissionRequestId,
        submissionMetadata,
        verificationEmail: verificationEmail,
    }

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Post,
                body: JSON.stringify(request),
            },
            true,
            true
        );
        if (!res.ok) {
            LogException(
                'CreateSubmission failed',
                new Error(res.statusText),
                {
                    entityId,
                    submissionRequestId,
                }
            );
            console.error('Failure creating new submission');
            console.error(`Attempted id ${entityId}`)
            console.error(res.statusText);

            throw new Error(res.statusText);
        }

        const responseObject: SubmissionDescriptor = await res.json();

        return responseObject.id;
    } catch (error) {
        LogException(
            'CreateSubmission failed',
            error,
            {
                entityId,
                submissionRequestId,
            }
        );

        console.error('Failure creating new submission');
        console.error(`Attempted id ${entityId}`)
        console.error(error);

        throw error;
    }
}

/**
 * Get the details of a submission from entityId and submissionId
 *
 * @param entityId  entityId for which submissions are being retrieved.
 *
 * @param submissionId submissionId for which submission details are being retrieved.
 *  
 * @result The submission details for this entity.  Will be undefined if an error occurs.
 */

export async function GetSubmissionDetail(entityId: string, submissionId: string): Promise<SubmissionDetail> {

    if (!entityId) throw new Error(`entityId is required.`)
    if (!submissionId) throw new Error(`submissionId is required.`)

    LogMessage(
        `GetSubmissionDetail`,
        SeverityLevel.Information,
        {
            entityId,
            submissionId,
        }
    );

    const url = `/api/${entityId}/Submissions/${submissionId}`;

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Get,
            },
            true,
            true
        );
        if (!res.ok) {
            console.error('Failed to retrieve submission detail');
            console.error(res.statusText);

            throw new Error(`${res.statusText} ${res.status}`);
        }

        const result: SubmissionDetail = await res.json();

        return result;
    } catch (error) {
        LogException(
            'GetSubmissionDetail failed',
            error,
            {
                entityId,
                submissionId,
            }
        );

        console.error('Failed to retrieve submission detail');
        console.error(error);

        throw error;
    }
}


/**
 * Get the list of submissions for an entity
 *
 * @param entityId  entityId for which submissions are being retrieved.
 *
 * @result List of submissions for this entity.  Will be undefined if an error occurs.
 */

export async function GetSubmissionList(entityId: string): Promise<Submission[] | undefined> {
    LogMessage(
        'GetSubmissionList',
        SeverityLevel.Information,
        {
            entityId,
        }
    );

    const url = `/api/Submissions?entityId=${entityId}`;

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Get,
            },
            true,
            true
        );
        if (!res.ok) {
            LogException(
                'GetSubmissionList failed',
                new Error(res.statusText),
                {
                    entityId,
                }
            );

            console.error('Failed to retrieve list of submissions');
            console.error(res.statusText);

            return undefined;
        }

        const result: Submission[] = await res.json();

        return result;
    } catch (error) {
        LogException(
            'GetSubmissionList failed',
            error,
            {
                entityId,
            }
        );

        console.error('Failed to retrieve list of submissions');
        console.error(error);

        return undefined;
    }
}

/**
 * Assigns an email address to a submission. This enables an email containing
 * a link to a summary of the borrower's submitted data being sent to the address
 * when the data is ready.
 *
 * @param entitId id of the organization associated with the submission.
 * @param submissionId id of the submission being modified.
 * @param emailAddress email address to which the link should be sent.
 *
 * @throws either FetchError from fetch request or Error
 */

export async function SetSubmissionEmail(
    entityId: string,
    submissionId: string,
    requestBody: RequestCustomerDataCopyParameters
): Promise<void> {
    if (!entityId) throw new Error(`entityId is required.`)
    if (!submissionId) throw new Error(`submissionId is required.`)

    LogMessage(
        'SetSubmissionEmail',
        SeverityLevel.Information,
        {
            entityId,
            submissionId
        }
    );

    const url = `/api/${entityId}/Submissions/${submissionId}/customerdatarequest`;

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Post,
                body: JSON.stringify(requestBody),
            },
            true,
            true
        );
        if (!res.ok) {
            LogException(
                'SetSubmissionEmail failed',
                new Error(res.statusText),
                {
                    entityId,
                    submissionId
                }
            );

            console.error('Failed to set submission email address');
            console.error(res.statusText);

            throw new Error(res.statusText);
        }
    } catch (error) {
        LogException(
            'SetSubmissionEmail failed',
            error,
            {
                entityId,
                submissionId
            }
        );

        console.error('Failed to set submission email address');
        console.error(error);

        throw error;
    }
}

export async function SearchSubmissions(
    startDate: Date,
    endDate: Date,
    businessId?: string,
    includeDeleted?: boolean,
): Promise<SubmissionSearchResult[]> {
    LogMessage(
        'SearchSubmissions',
        SeverityLevel.Information,
        {
            startDate: startDate.toString(),
            endDate: endDate.toString(),
            businessId
        }
    );

    const requestBody: SubmissionSearchRequest = {
        startCreationDate: startDate.toISOString(),
        endCreationDate: endDate.toISOString(),
        entityId: businessId,
        includeDeleted,
    }

    let url = `/api/submissions/search`;

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Post,
                body: JSON.stringify(requestBody),
            },
            true,
            true
        );
        if (!res.ok) {
            LogException(
                'SearchSubmissions failed',
                new Error(res.statusText),
                {
                    startDate: startDate.toString(),
                    endDate: endDate.toString(),
                    businessId
                }
            );

            console.error('Failed to search submissions');
            console.error(res.statusText);

            throw new Error(res.statusText);
        }

        const response: SubmissionSearchResponse = await res.json();

        return response.submissions;
    } catch (error) {
        LogException(
            'SearchSubmissions failed',
            error,
            {
                startDate: startDate.toString(),
                endDate: endDate.toString(),
                businessId
            }
        );

        console.error('Failed to search submissions');
        console.error(error);

        throw error;
    }
}

/**
 * @returns List of submissions that have been marked by the user as requiring more input from a lender side user to complete
 */

export async function ListSubmissionsPendingMoreLenderInput(
    includeDeleted?: boolean,
): Promise<SubmissionSearchResponse> {
    LogMessage(
        'ListSubmissionsPendingMoreLenderInput',
        SeverityLevel.Information
    );

    let startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);

    let url = `/api/submissions/pendinguserintervention?startCreationDate=${startDate.toISOString()}`;

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Get,
            },
            true,
            true
        );
        if (!res.ok) {
            LogException(
                'ListSubmissionsPendingMoreLenderInput failed',
                new Error(res.statusText)
            );

            console.error('ListSubmissionsPendingMoreLenderInput failed');
            console.error(res.statusText);

            throw new Error(res.statusText);
        }

        const response: SubmissionSearchResponse = await res.json();

        return response;
    } catch (error) {
        LogException(
            'ListSubmissionsPendingMoreLenderInput failed',
            error,
        );

        console.error('ListSubmissionsPendingMoreLenderInput failed');
        console.error(error);

        throw error;
    }
}

/**
 * Lists submissions that are in the pending state.  These can be direct connections or also those created
 * from a shared link.
 * 
 * @param returnAll - optional. Should all submissions be returned regardless of whether the current user 
 * is granted access to the workspace to which they belong. Default is false. If this is true, the user
 * must be an admin or the call to the controller will fail.
 * @param startDate - optional, specifies the earliest date to include submissions from.  If not provided 
 * all submissions in the pending state are returned.
 * @param pendingSubmissionsVersion - optional, the returned pending submissions version returned from the
 * controller layer. 
 */

export async function ListPendingSubmissions(
    returnAll?: boolean,
    startDate?: Date,
    pendingSubmissionsVersion?: number,
): Promise<PendingSubmissionsResults> {
    LogMessage(
        'ListPendingSubmissions',
        SeverityLevel.Information,
        {
            startDate: startDate?.toString() || 'Not provided'
        }
    );

    let url = `/api/submissions/listpending`;
    let needsAmpersand = false;

    if (!startDate) {
        startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 1);
    }

    if (!!startDate || !!returnAll || !!pendingSubmissionsVersion) {
        url += '?'
        if (!!startDate) {
            url += `startCreationDate=${startDate.toISOString()}`;
            needsAmpersand = true;
        }
        if (!!returnAll) {
            if (needsAmpersand) {
                url += '&';
            }
            url += `returnAll=true`;
            needsAmpersand = true;
        }
        if (!!pendingSubmissionsVersion) {
            if (needsAmpersand) {
                url += '&';
            }
            url += `tenantPendingSubmissionVersion=${pendingSubmissionsVersion}`;
            needsAmpersand = true;
        }
    }

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Get
            },
            true,
            true
        );
        if (!res.ok) {
            LogException(
                'ListPendingSubmissions failed',
                new Error(res.statusText),
                {
                    startDate: startDate?.toString() || 'Not provided'
                }
            );

            console.error('Failed to search ListPendingSubmissions');
            console.error(res.statusText);

            throw new Error(res.statusText);
        }

        const submissionsResult: PendingSubmissionsResults = await res.json();

        return submissionsResult;
    } catch (error) {
        LogException(
            'ListPendingSubmissions failed',
            error,
            {
                startDate: startDate?.toString() || 'Not provided'
            }
        );

        console.error('Failed to list pending submissions');
        console.error(error);

        throw error;
    }
}

/**
 * Get the id of the newest complete submission in a workspace
 * 
 * @param workspaceId - The workspace in question or undefined if there are no complete submissions
 */

export async function GetNewestSubmissionIdForWorkspace(
    workspaceId: string
): Promise<string | undefined> {
    LogMessage(
        'GetNewestSubmissionIdForWorkspace',
        SeverityLevel.Information,
        {
            workspaceId,
        }
    );

    let url = `/api/submissions/newest?entityId=${workspaceId}`;

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Get
            },
            true,
            true
        );
        if (!res.ok) {
            LogException(
                'GetNewestSubmissionIdForWorkspace failed',
                new Error(res.statusText),
                {
                    workspaceId,
                }
            );

            console.error('GetNewestSubmissionIdForWorkspace failed');
            console.error(res.statusText);

            throw new Error(res.statusText);
        }

        const result: EntityNewestSubmissionResponse = await res.json();

        if (result.submissionId === EmptyGuid) {
            return undefined;
        }

        return result.submissionId;
    } catch (error) {
        LogException(
            'GetNewestSubmissionIdForWorkspace failed',
            error,
            {
                workspaceId,
            }
        );

        console.error('GetNewestSubmissionIdForWorkspace failed');
        console.error(error);

        throw error;
    }
}

/**
 * Delete a submission
 * 
 * @param submissionId - Id of the submission to mark as deleted
 * @returns true if submission deleted
 */

export async function DeleteSubmission(
    submissionId: string
): Promise<void> {
    LogMessage(
        'Delete a submission',
        SeverityLevel.Information,
        {
            submissionId,
        }
    );

    let url = `/api/submissions/${submissionId}`

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Delete,
            },
            true,
            true
        );
        if (!res.ok) {
            LogException(
                'DeleteSubmission failed',
                new Error(res.statusText),
                {
                    submissionId,
                }
            );

            console.error('DeleteSubmission failed');
            console.error(res.statusText);

            throw new Error(res.statusText);
        }
    } catch (error) {
        LogException(
            'DeleteSubmission failed',
            error,
            {
                submissionId,
            }
        );

        console.error('DeleteSubmissions failed');
        console.error(error);

        throw error;
    }
}

/**
 * Restore a submission from deleted
 * 
 * @param submissionId - Id of the submission to restore
 * @returns true if submission is now restored
 */

export async function RestoreSubmission(
    submissionId: string
): Promise<void> {
    LogMessage(
        'Restore a submission',
        SeverityLevel.Information,
        {
            submissionId,
        }
    );

    let url = `/api/submissions/restore/${submissionId}`

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Put,
            },
            true,
            true
        );
        if (!res.ok) {
            LogException(
                'RestoreSubmission failed',
                new Error(res.statusText),
                {
                    submissionId,
                }
            );

            console.error('RestoreSubmission failed');
            console.error(res.statusText);

            throw new Error(res.statusText);
        }
    } catch (error) {
        LogException(
            'RestoreSubmission failed',
            error,
            {
                submissionId,
            }
        );

        console.error('RestoreSubmissions failed');
        console.error(error);

        throw error;
    }
}

/**
 * Set portal status for a submission
 * 
 * @param submissionId - Id of the submission to restore
 * @returns true if submission is now restored
 */

export async function SetSubmissionPortalStatus(
    entityId: string,
    submissionId: string,
    status: SubmissionPortalStatus,
    datasourceNameId: string
): Promise<Submission> {
    LogMessage(
        'Set submission portal status',
        SeverityLevel.Information,
        {
            entityId,
            submissionId,
            status,
        }
    );

    let url = `/api/submissions/${entityId}/${submissionId}`;

    const requestBody: Operation[] = [
        {
            value: status,
            path: 'portalstatus',
            op: 'replace'
        },
        {
            value: datasourceNameId,
            path: 'datasourcenameid',
            op: 'replace'
        }
    ]

    try {
        const res = await fetchWrapper(url,
            {
                method: FetchMethod.Patch,
                body: JSON.stringify(requestBody),
            },
            false,
            true,
            // for nocache
            true,
            // for jsonpatchheaders
            true
        );
        if (!res.ok) {
            LogException(
                'SetSubmissionPortalStatus failed',
                new Error(res.statusText),
                {
                    entityId,
                    submissionId,
                    status,
                }
            );

            console.error('SetSubmissionPortalStatus failed');
            console.error(res.statusText);

            throw new Error(res.statusText);
        }

        return await res.json();
    } catch (error) {
        LogException(
            'SetSubmissionPortalStatus failed',
            error,
            {
                entityId,
                submissionId,
                status,
            }
        );

        console.error('RestoreSetSubmissionPortalStatusSubmissions failed');
        console.error(error);

        throw error;
    }
}
